/**
 * Expiry Exceptions Service
 * Handles fetching/managing unmatched expiries and processing failures
 */

import { apiClient } from './client';
import type { ExpiryExceptionsData } from '../../types/expiry';

const BASE = '/api/v1/clients';

export const expiryExceptionsService = {
  /**
   * Get all active exceptions (unmatched + processing failures) for badge & modal
   */
  async getExceptions(clientId: string) {
    return apiClient.get<ExpiryExceptionsData>(`${BASE}/${clientId}/expiry-exceptions`);
  },

  /**
   * Manually match an unmatched expiry to a specific trade.
   * entityId may differ from clientId when the trade belongs to a child entity.
   */
  async manualMatch(clientId: string, expiryId: string, tradeId: string, entityId?: string) {
    const qs = entityId ? `?entity_id=${encodeURIComponent(entityId)}` : '';
    return apiClient.post<{ tradeId: string; tradeNumber: string; matched: boolean }>(
      `${BASE}/${clientId}/expiry-manual-match/${expiryId}/${tradeId}${qs}`
    );
  },

  /**
   * Dismiss an unmatched expiry (keeps document, sets dismissed flag)
   */
  async dismissExpiry(clientId: string, expiryId: string) {
    return apiClient.post<{ expiryId: string; dismissed: boolean }>(
      `${BASE}/${clientId}/expiry-dismiss/${expiryId}`
    );
  },

  /**
   * Retry processing a failed email
   */
  async retryProcessing(clientId: string, emailId: string) {
    return apiClient.post<{ matched: boolean; tradesProcessed: number }>(
      `${BASE}/${clientId}/expiry-retry/${emailId}`
    );
  },

  /**
   * Dismiss a processing failure
   */
  async dismissFailure(clientId: string, emailId: string) {
    return apiClient.post<{ emailId: string; dismissed: boolean }>(
      `${BASE}/${clientId}/expiry-dismiss-failure/${emailId}`
    );
  },
};
